import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import BeneluxHero from './components/BeneluxHero';
import BeneluxContents from './components/BeneluxContents';
import BeneluxClauses from './components/BeneluxClauses';
import BeneluxFaq from './components/BeneluxFaq';
import BeneluxInternalLinks from './components/BeneluxInternalLinks';

const faqs = [
  {
    question: 'Werkt Pactorae voor zowel België als Nederland?',
    answer: 'Ja, Pactorae is specifiek ontworpen voor freelancers en ZZP\'ers in zowel België als Nederland. De generator houdt rekening met de specifieke juridische vereisten van beide landen, inclusief het ondernemingsnummer en btw-nummer voor België, en het KvK-nummer voor Nederland.',
  },
  {
    question: 'Wat is het verschil tussen Belgisch en Nederlands contractrecht?',
    answer: 'Hoewel beide landen een vergelijkbaar rechtssysteem hebben, zijn er belangrijke verschillen. In België is het Burgerlijk Wetboek van toepassing, terwijl in Nederland het Burgerlijk Wetboek (BW) geldt. Betalingstermijnen, aansprakelijkheidsregels en de vereisten voor een geldig contract kunnen verschillen.',
  },
  {
    question: 'Kan ik contracten in beide talen maken?',
    answer: 'Ja, Pactorae ondersteunt het Nederlands voor beide markten. Contracten worden opgesteld in het Nederlands, wat de standaardtaal is voor zakelijke overeenkomsten in zowel België als Nederland.',
  },
  {
    question: 'Is Pactorae gratis?',
    answer: 'Ja, je kunt gratis een professioneel freelance contract genereren via Pactorae. De basisgenerator is volledig gratis beschikbaar voor freelancers en ZZP\'ers in de Benelux.',
  },
  {
    question: 'Welke contracttypes zijn beschikbaar voor de Benelux?',
    answer: 'Pactorae biedt de volgende contracttypes aan: freelance overeenkomst, NDA (geheimhoudingsverklaring), algemene voorwaarden en consultancy overeenkomst. Elk contract wordt op maat gemaakt voor jouw specifieke situatie en het gekozen land.',
  },
];

const schemaData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': 'https://pactorae.com/freelance-contract-template-benelux',
      url: 'https://pactorae.com/freelance-contract-template-benelux',
      name: 'Freelance Contract Generator voor België en Nederland | Pactorae',
      description: 'Pactorae is de contract generator voor freelancers en ZZP\'ers in de Benelux. Genereer gratis een professioneel freelance contract voor België of Nederland.',
      inLanguage: 'nl',
      isPartOf: { '@type': 'WebSite', url: 'https://pactorae.com', name: 'Pactorae' },
    },
    {
      '@type': 'FAQPage',
      mainEntity: faqs?.map((faq) => ({
        '@type': 'Question',
        name: faq?.question,
        acceptedAnswer: { '@type': 'Answer', text: faq?.answer },
      })),
    },
  ],
};

const FreelanceContractBeneluxPage = () => (
  <>
    <Helmet>
      <html lang="nl" />
      <title>Freelance Contract Generator voor België en Nederland | Pactorae</title>
      <meta
        name="description"
        content="Pactorae is de contract generator voor freelancers en ZZP'ers in de Benelux. Genereer gratis een professioneel freelance contract voor België of Nederland — juridisch correct en direct te gebruiken."
      />
      <meta
        name="keywords"
        content="freelance contract België Nederland, contract generator Benelux, zzp contract generator, freelance overeenkomst Benelux, contract template België Nederland"
      />
      <link rel="canonical" href="https://pactorae.com/freelance-contract-template-benelux" />
      <meta name="robots" content="index, follow" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://pactorae.com/freelance-contract-template-benelux" />
      <meta property="og:title" content="Freelance Contract Generator voor België en Nederland | Pactorae" />
      <meta
        property="og:description"
        content="Pactorae is de contract generator voor freelancers en ZZP'ers in de Benelux. Gratis en juridisch correct."
      />
      <meta property="og:site_name" content="Pactorae" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Freelance Contract Generator voor België en Nederland | Pactorae" />
      <meta
        name="twitter:description"
        content="Pactorae is de contract generator voor freelancers en ZZP'ers in de Benelux. Gratis en juridisch correct."
      />
      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
    </Helmet>
    <main>
      <BeneluxHero />
      <article className="max-w-4xl mx-auto px-6 py-16">
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#2F4E6F] mb-4">Pactorae: contract generator voor de Benelux</h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-4">
            Pactorae is speciaal ontworpen voor freelancers en ZZP'ers in België en Nederland. Of je nu als zelfstandige in Brussel werkt of als zzp'er in Amsterdam — onze generator maakt in 30 seconden een professioneel contract op maat voor jouw situatie en het recht van jouw land.
          </p>
          <p className="text-gray-700 leading-relaxed">
            De generator houdt rekening met de specifieke vereisten van beide landen: voor Belgische freelancers worden het ondernemingsnummer en btw-nummer verwerkt in het contract, terwijl voor Nederlandse ZZP'ers het KvK-nummer en de Nederlandse btw-regelgeving worden toegepast.
          </p>
        </section>
        <BeneluxContents />
        <BeneluxClauses />
        <BeneluxFaq />
        <BeneluxInternalLinks />
        {/* Final CTA */}
        <section className="text-center py-12 bg-gradient-to-br from-[#2F4E6F] to-[#1e3550] rounded-2xl px-8">
          <h2 className="text-2xl font-bold text-white mb-4">Klaar om jouw contract te genereren?</h2>
          <p className="text-blue-100 mb-8 max-w-lg mx-auto">
            Genereer gratis een professioneel freelance contract voor België of Nederland. Klaar in 30 seconden.
          </p>
          <Link
            to="/contract-generator"
            className="inline-block bg-[#FF8C00] hover:bg-[#e07b00] text-white font-semibold px-10 py-4 rounded-lg text-lg transition-colors duration-200 shadow-lg"
          >
            Genereer jouw contract gratis
          </Link>
        </section>
      </article>
    </main>
  </>
);

export default FreelanceContractBeneluxPage;
